import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import AdsSwiper from "../../Ads/components/detailComponents/AdsSwiper";
import SelectOptionButton from "../../AutoMarketing/components/detailComponents/SelectOptionButton";
import InstaTextArea from "../../AutoMarketing/components/detailComponents/InstaTextArea";
import { toCanvas } from "html-to-image";
import couponImg from "../../../assets/image/icon/coupon.png";
import {
    addAppMessageListener,
    postToApp,
} from "../../../bridges/appBridge";
import useFormattedDateOnce from "../../../hooks/useFormattedDate";
import platformFromChannel from "../../../hooks/getPlatform";
import useStrictBackBlock from "../../../bridges/useStrictBackBlock";
import { useRecordsStore } from "../../../stores/useReordsStore";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import home from "../../../assets/icons/System/close.svg";
import { SplitRowHalf } from "../../../assets/components/ButtonGroup";
import retry from "../../../assets/icons/System/repeat_primary.svg";
import { Chip } from "../../../assets/components/Chip";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import { useToastOffset } from "../../../hooks/useToastOffset";
import checkOn from "../../../assets/icons/Select/check_checked.svg"
import checkOff from "../../../assets/icons/Select/check_unchecked.svg"
import TokenPopup from "../../Ads/components/detailComponents/TokenPopup";
import { TwinColumn } from "../../../assets/components/ButtonGroup";
import Popup from "../../../assets/components/PopupGroup";
import { useAddAIGeneratedWatermark } from "../../../hooks/useAddAIGeneratedWatermark";
import useTokenInfo from "../../../hooks/useTokenInfo";

const titleOptions = {
    1: "매장 홍보",
    2: "상품 소개",
    3: "이벤트",
    4: "감사 인사",
};

const channelOptions = {
    1: "카카오톡",
    2: "인스타 스토리",
    3: "인스타 게시물",
    4: "블로그",
    5: "MMS(문자)",
    6: "네이버 밴드",
    7: "X(트위터)",
};

const ageOptions = {
    1: "10대",
    2: "20대",
    3: "30대",
    4: "40대",
    5: "50대",
    6: "60대 이상",
};

const themeLabelMap = { 1: "매장 홍보", 2: "상품 소개", 3: "이벤트" };

const AGE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(ageOptions).map(([k, v]) => [v, String(k)])
);
const TITLE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(titleOptions).map(([k, v]) => [v, String(k)])
);
const CHANNEL_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(channelOptions).map(([k, v]) => [v, String(k)])
);

const normalizeAgeCode = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "3";
  if (AGE_LABEL_TO_CODE[s]) return AGE_LABEL_TO_CODE[s];
  if (/^[1-6]$/.test(s)) return s;
  if (/^(10|20|30|40|50|60)$/.test(s)) return String(Number(s) / 10);
  return "3";
};

const normalizeTitleCode = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "1";
  if (TITLE_LABEL_TO_CODE[s]) return TITLE_LABEL_TO_CODE[s];
  if (/^[1-4]$/.test(s)) return s;
  return "1";
};

const normalizeChannelCode = (channel, subChannel) => {
  const c = String(channel ?? "").trim();
  const sub = String(subChannel ?? "").trim();

  if (/^[1-7]$/.test(c)) return c;
  if (CHANNEL_LABEL_TO_CODE[c]) return CHANNEL_LABEL_TO_CODE[c];

  // Step2 라벨 형태로 올 때 (인스타그램 + subChannel)
  if (c === "인스타그램") {
    if (sub === "스토리") return "2";
    if (sub === "게시물") return "3";
  }

  // "인스타 스토리" 같은 형태로 올 때
  if (c.includes("인스타") && c.includes("스토리")) return "2";
  if (c.includes("인스타") && c.includes("게시물")) return "3";

  return "1";
};

const channelCodeToStep2Parts = (code) => {
  const c = String(code ?? "").trim();
  if (c === "2") return { channel: "인스타그램", subChannel: "스토리" };
  if (c === "3") return { channel: "인스타그램", subChannel: "게시물" };
  return { channel: channelOptions[c] || "", subChannel: "" };
};

const ManualMarketingAIResult = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [checkedBox] = useState(false);
    const addWatermark = useAddAIGeneratedWatermark();

    // 글로벌 기록 스토어 저장용
    const addRecord = useRecordsStore((s) => s.addRecord);

    const navState = location.state;
    const { tokenInfo, fetchTokenInfo } = useTokenInfo();

    const base = Array.isArray(navState) ? (navState[0] ?? {}) : (navState?.data ?? navState ?? {});
    const isRegen = Boolean((!Array.isArray(navState) && navState?.isRegen) ?? base?.isRegen); // 재생성 모드 여부
    const [tokenAll, setTokenAll] = useState(() => Number(base?.tokenAll ?? 0));

    useEffect(() => {
        const v = Number(tokenInfo?.total);
        if (Number.isFinite(v)) setTokenAll(v);
    }, [tokenInfo?.total]);

    // 초기 값 (카메라 결과는 state[0] 구조)
    let {
        store_business_number: storeBusinessNumber,
        origin_image,
        style: initStyle,
        title: initTitle,
        channel: initChannel,
        insta_copyright,
        copyright,
        core_f,
        store_name,
        road_name,
        district_name,
        main,
        temp,
        menu,
        detail_category_name,
        prompt,
        aiThumbnail,
        customText,
        event_title,
        ad_text,
        ad_text_theme,
        ad_text_override,
    } = base;

    // 사람이 고른 값(age/channel/style/theme)을 우선 사용, ai*는 없을 때만 fallback
    const normAgeCode = normalizeAgeCode(core_f ?? base?.aiAge ?? base?.age);
    const normChannelCode = normalizeChannelCode(initChannel ?? base?.aiChannel ?? base?.channel, base?.subChannel);
    const normTitleCode = normalizeTitleCode(initTitle ?? base?.aiTitle ?? base?.theme);
    const normStyleCode = String(initStyle ?? base?.aiStyle ?? base?.style ?? "1");

    core_f = normAgeCode;
    initTitle = normTitleCode;
    initChannel = normChannelCode;
    initStyle = normStyleCode;

    if (initTitle === "3" && event_title?.trim()) {
        if (!(copyright || "").includes("제목")) {
            copyright = `제목 : ${event_title}\n내용 : ${(copyright ?? "").trim()}`;
        }
    }

    const [rePrompt] = useState(prompt);
    const imageSrc = origin_image?.[0] ? `data:image/png;base64,${origin_image[0]}` : "";
    const [weekday, setWeekday] = useState("");
    const { fullDate, onlyDate, onlyTime } = useFormattedDateOnce();
    const [, setSharePending] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);

    // 팝업창
    const [popupOpen, setPopupOpen] = useState(false);
    const onOut = () => {
        setPopupOpen(false);
    }
    const [noPopup, setNoPopup] = useState(false); // 기본: 체크 ON
    // const popupLocal = localStorage.getItem("popup");
    const NoPopup = () => {
        setNoPopup((prev) => !prev);
    };

    // 토큰 구매 후 돌아올 state 저장
    const tokenReturnStateRef = useRef(null);

    const openTokenPopup = () => {
        const payload = {
            data: {
            store_business_number: storeBusinessNumber,
            origin_image,
            style: String(selections.style),
            title: String(selections.title),
            channel: String(selections.channel),
            core_f: String(selections.age),
            insta_copyright,
            copyright,
            event_title,
            store_name,
            road_name,
            district_name,
            main,
            temp,
            menu,
            detail_category_name,
            prompt: rePrompt,
            aiThumbnail,
            ad_text,
            ad_text_override: currentAdText ?? "",
            use_override: true,
            ad_text_theme:
                adTextTheme ?? themeLabelMap[String(selectedTitle)] ?? null,
            customText:
                (currentAdText ?? "").trim() ||
                (ad_text ?? "").trim() ||
                (copyright ?? "").trim() ||
                "",
            },
        };

        tokenReturnStateRef.current = payload;
        setShowTokenModal(true);
    };

    useEffect(() => {
        if (!showTokenModal) fetchTokenInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTokenModal]);

    // 1) 상하단 고정 훅
    const {
        headerRef,
        footerRef,
        headerSpace,
        bottomSpace,
    } = useFixedLayout();

    // 2) 토스트 위치 훅 (footer 기준 + 키보드 대응)
    const toastOffset = useToastOffset({
        footerRef,
        bottomSpace,
        gap: 8,
    });


    useEffect(() => {
        const daysOfWeek = [
            "일요일",
            "월요일",
            "화요일",
            "수요일",
            "목요일",
            "금요일",
            "토요일",
        ];
        const today = new Date().getDay();
        setWeekday(daysOfWeek[today]);
    }, []);

    function loadReadyImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                if (img.decode) {
                    img.decode()
                        .then(() => resolve(img))
                        .catch(() => resolve(img));
                } else {
                    resolve(img);
                }
            };
            img.onerror = reject;
            img.src = src;
        });
    }

    function nextFrame() {
        return new Promise((r) =>
            requestAnimationFrame(() => requestAnimationFrame(r))
        );
    }

    // 바텀시트 관련 state 삭제 (페이지 이동으로 대체)
    // const [activeOption, setActiveOption] = useState(null);
    // const openOption = (key) => setActiveOption(key);
    // const closeSheet = () => setActiveOption(null);

    // 바텀 시트 초기 값 설정
    // 나이
    const selectedAge = normAgeCode;

    // 주제
    const selectedTitle = normTitleCode;

    // 입력값 (주제별 인스타 텍스트)
    const initialAdText = ad_text_override ?? ad_text ?? customText ?? "";
    const currentAdText = initialAdText;
    const adTextTheme = ad_text_theme || themeLabelMap[String(selectedTitle)] || null;

    const safeCopyright = (copyright || "").replace(/["']/g, "").trim();

    // 채널
    const selectedChannel = normChannelCode;

    // 스타일
    const selectedStyle = normStyleCode;

    const selections = {        
        style: String(selectedStyle),
        title: String(selectedTitle),
        channel: String(selectedChannel),
        age: String(selectedAge),
    };

    // 팝업창
    // const [, setShowRegenPopup] = useState(false);
    const [skipRegenPopup, setSkipRegenPopup] = useState(() => {
        if (!isRegen) return false;
        try {
            const v = localStorage.getItem("re_popup");
            // 서버에서 bool → localStorage 저장 시 "true" / "false" 문자열이라고 가정
            return v === "true";
        } catch {
            return false;
        }
    });

    // ✅ 키 (유저별로 분리 추천)
    const REGEN_SAME_OPTION_KEY = `regen_same_option:${localStorage.getItem("user_id") || "guest"}`;

    // ✅ 체크박스 state: localStorage 값으로 초기화
    const [regenWithSameOption, setRegenWithSameOption] = useState(() => {
        try {
            const v = localStorage.getItem(REGEN_SAME_OPTION_KEY);
            return v === null ? true : v === "true"; // 없으면 기본 true
        } catch {
            return true;
        }
    });

    // ✅ 값이 바뀔 때마다 저장
    useEffect(() => {
        try {
            localStorage.setItem(REGEN_SAME_OPTION_KEY, String(regenWithSameOption));
        } catch { }
    }, [REGEN_SAME_OPTION_KEY, regenWithSameOption]);


    // 옵션 태그 표시용
    const selectedTags = [
        { key: "age", prefix: "고객", label: ageOptions[selections.age] },
        {
            key: "channel",
            prefix: "채널",
            label: channelOptions[selections.channel],
        },
        {
            key: "title",
            prefix: "맞춤주제",
            label: titleOptions[selections.title],
        },
        // { key: "style", prefix: "스타일", label: styleOptions[selections.style] },
    ];

    // 생성된 이미지 처리
    const [checkImages, setCheckImages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const returnHome = () => {
        navigate(`/ads/main`, { state: { storeBusinessNumber } });
    };

    // 인스타 텍스트
    const [instaText, setInstaText] = useState(insta_copyright || "");
    const handleTextChange = (e) => {
        setInstaText(e.target.value);
    };

    // 🔹 옵션 편집 페이지로 이동 (AutoMarketingResult의 handleEdit 패턴)
    const handleEdit = () => {
        const { channel: step2Channel, subChannel: step2Sub } =
        channelCodeToStep2Parts(selections.channel);
        
        navigate(`/manual/marketing/ai/regen`, {
            state: {
                isRegen: true,
                data: {
                    origin_image,
                    style: String(selections.style),
                    aiStyle: String(selections.style),
                    title: String(selections.title),
                    aiTitle: String(selections.title),
                    theme: titleOptions[String(selections.title)] || "",
                    channel: step2Channel,
                    subChannel: step2Sub,
                    aiChannel: String(selections.channel),
                    age: ageOptions[String(selections.age)] || "",
                    aiAge: String(selections.age),
                    aiThumbnail : aiThumbnail,
                    prompt: rePrompt,
                    main,
                    temp,
                    store_name,
                    detail_category_name,
                    custom_menu: menu,
                    road_name,
                    district_name,
                    store_business_number: storeBusinessNumber,
                    ad_text,
                    ad_text_override: currentAdText ?? "",
                    use_override: true,
                    ad_text_theme:
                        adTextTheme ??
                        themeLabelMap[String(selectedTitle)] ??
                        null,
                    copyright: copyright ?? "",
                    customText: (currentAdText ?? "").trim() || (ad_text ?? "").trim() || (copyright ?? "").trim() || "",
                },
            },
        });
    };

    // 🔹 다시 생성하기 (기존 reGenerate 유지 – flat state)
    const reGenerate = () => {        
        const ageCode = String(selections.age);                  // "1"
        const ageLabel = ageOptions[ageCode] || "";              // "10대"
        const titleCode = String(selections.title);              // "1"
        const titleLabel = titleOptions[titleCode] || "";        // "매장 홍보"
        const channelCode = String(selections.channel);          // "6"
        const channelLabel = channelOptions[channelCode] || "";  // "네이버 밴드"

        // 인스타만 subChannel 필요 (백엔드가 sub_channel 보고 2/3 결정)
        const { channel: step2Channel, subChannel: step2Sub } =
            channelCodeToStep2Parts(channelCode);
            
        navigate(`/manual/marketing/ai/loading`, {
            state: {
                theme: titleLabel,
                channel: step2Channel || channelLabel,
                subChannel: step2Sub || "",
                age: ageLabel,
                aiAge: ageCode,
                aiTitle: titleCode,
                aiChannel: channelCode,
                aiStyle: String(selections.style),
                style: String(selections.style),
                title: titleCode,
                prompt: rePrompt,
                main,
                temp,
                store_name,
                detail_category_name,
                custom_menu: menu,
                road_name,
                district_name,
                store_business_number: storeBusinessNumber,
                ad_text_override: currentAdText ?? "",
                use_override: true,
                ad_text_theme:
                    adTextTheme ?? themeLabelMap[String(selectedTitle)] ?? null,
            },
        });
    };

    // 🔹 “다시 생성하기” 버튼 클릭 시 → 팝업만 열기
    const handleRegenClick = () => {
        if (tokenAll <= 0) {
            openTokenPopup();
            return;
        }
        
        if (!isRegen) {
            reGenerate();
            return;
        }
        
        // 옵션 변경 모드면 바로 옵션 설정 화면으로 이동
        if (!regenWithSameOption) {
            handleEdit();
            return;
        }

        // 옵션 그대로 재생성 + 토큰 차감 모드
        if (skipRegenPopup) {
            // 이미 re_popup == true → 팝업 없이 바로 재생성
            reGenerate();
            return;
        }

        // 처음 또는 아직 re_popup 저장 안 된 상태 → 안내 팝업
        setPopupOpen(true);
    };

    // 🔹 팝업 > 옵션 변경하기
    // const handleRegenChangeOption = () => {
    //     setShowRegenPopup(false);
    //     handleEdit();
    // };

    // 이미지 선택
    const handleImageClick = (index) => {
        setCheckImages([index]);
    };

    // 템플릿 -> 이미지 캡쳐
    const captureImageFromTemplate = async (
        index,
        titlePath,
        useOptionPath
    ) => {
        const templateElement = document.getElementById(
            `template_${titlePath}_${useOptionPath}_${index}`
        );
        if (!templateElement) return null;

        const cursorElements =
            templateElement.querySelectorAll(".blinking-cursor");
        cursorElements.forEach((el) => {
            el.classList.remove("blinking-cursor");
            el.classList.add("no-blinking");
        });

        const editableElements =
            templateElement.querySelectorAll(".editable-text");
        editableElements.forEach((el) => {
            el.setAttribute("data-prev-border", el.style.border || "");
            el.style.border = "none";
        });

        let couponEl = null;
        try {
            if (checkedBox) {
                await loadReadyImage(couponImg);
                couponEl = document.createElement("img");
                couponEl.src = couponImg;
                couponEl.alt = "쿠폰";
                couponEl.className = "w-16 h-16 object-contain";
                couponEl.style.position = "absolute";
                couponEl.style.bottom = "28px";
                couponEl.style.right = "20px";
                couponEl.style.zIndex = "99";
                if (getComputedStyle(templateElement).position === "static") {
                    templateElement.style.position = "relative";
                }
                templateElement.appendChild(couponEl);
                await nextFrame();
            }
            const canvas = await toCanvas(templateElement, { cacheBust: true });
            return canvas.toDataURL("image/png", 1.0);
        } catch (e) {
            console.error("캡처 실패:", index, e);
            return null;
        } finally {
            if (couponEl && couponEl.parentNode)
                templateElement.removeChild(couponEl);
            cursorElements.forEach((el) => {
                el.classList.remove("no-blinking");
                el.classList.add("blinking-cursor");
            });
            editableElements.forEach((el) => {
                el.style.border = el.getAttribute("data-prev-border") || "";
                el.removeAttribute("data-prev-border");
            });
        }
    };

    // 업로드
    const onUpload = async () => {
        setIsLoading(true);

        try {
            let useOptionPath = "";
            let titlePath = "";

            if (selections.title === "1" || selections.title === "2") {
                titlePath = "intro";
            } else if (selections.title === "3") {
                titlePath = "event";
            }

            if (selections.channel === "1" || selections.channel === "2" || selections.channel === "5") {
                useOptionPath = "4to7";
            } else if (
                selections.channel === "3" ||
                selections.channel === "4" ||
                selections.channel === "6" ||
                selections.channel === "7"
            ) {
                useOptionPath = "1to1";
            }

            const index = checkImages?.[0];
            const key = `template_${titlePath}_${useOptionPath}_${index}`;

            if (index === undefined || index === null) {
                console.log({ index });
                alert("템플릿이 적용된 이미지를 선택해 주세요.");
                setIsLoading(false);
                return;
            }

            let imageData;
            const exporter = window.__templateExports?.[key];

            try {
                if (typeof exporter === "function") {
                    imageData = await exporter();
                } else {
                    imageData = await captureImageFromTemplate(
                        index,
                        titlePath,
                        useOptionPath
                    );
                }
            } catch (e) {
                console.error("exporter/capture 실패:", e);
                imageData = await captureImageFromTemplate(
                    index,
                    titlePath,
                    useOptionPath
                );
            }

            if (index === 0) {
                imageData = imageSrc;
            }

            if (!imageData) {
                alert("이미지 생성 중 오류가 발생했습니다.");
                return;
            }

            const watermarkedImageData = await addWatermark(imageData);

            const manualDateRange = [onlyDate, onlyDate];
            const userId = localStorage.getItem("user_id");
            const ageForSave = /^[1-6]$/.test(String(selections.age)) ? `${selections.age}0` : String(selections.age);
            
            const data = {
                title: String(selections.title),
                age: ageForSave,
                style: String(selections.style),
                channel: String(selections.channel),
                repeat: "없음",
                date_range: manualDateRange,
                upload_time: onlyTime,
                alert_check: false,
                user_id: userId,
                image: watermarkedImageData,
                type: "manual",
                prompt: rePrompt,
                insta_copyright: instaText || "",
                copyright: copyright,
            };

            const response = await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/auto/app/save`,
                data,
                { headers: { "Content-Type": "application/json" } }
            );

            const { success, imageUrl } = response.data || {};
            if (!success || !imageUrl) {
                alert("저장 실패");
                return;
            }

            const newRecord = {
                image_path: imageUrl,
                title: String(selections.title),
                age: Number(selections.age),
                style: String(selections.style),
                channel: Number(selections.channel),
                repeat_time: "없음",
                start_date: Array.isArray(manualDateRange)
                    ? manualDateRange[0]
                    : undefined,
                end_date: Array.isArray(manualDateRange)
                    ? manualDateRange[1]
                    : undefined,
                prompt: rePrompt,
                insta_copyright: instaText,
                copyright: copyright,
            };

            addRecord(newRecord);

            const requestId = Math.random().toString(36).slice(2);
            const platform = platformFromChannel(String(selections.channel));
            let link = "";

            if (platform === "KAKAO") {
                const name = (localStorage.getItem("storeName") || "").trim();
                const region = (localStorage.getItem("roadName") || "").trim();
                if (name && region) {
                    const label = `${region} ${name}`.trim();
                    const long = `https://map.naver.com/p/search/${encodeURIComponent(
                        label
                    )}`;
                    link = await axios
                        .post(
                            `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/g`,
                            { url: long }
                        )
                        .then((r) => r.data?.short || "")
                        .catch(() => "");
                }
            }

            setSharePending(true);
            postToApp({
                type: "share.toChannel",
                requestId,
                social: platform,
                data: {
                    imageUrl,
                    caption: instaText || "",
                    couponEnabled: false,
                    ...(platform === "KAKAO" && link && { link }),
                },
            });

            if (!window.ReactNativeWebView) {
                console.log("[DEV] START_SHARE", {
                    platform,
                    caption: instaText,
                    imageUrl: imageUrl.slice(0, 64) + "...",
                });

                navigate("/manual/marketing/final", {
                    state: {
                        image: watermarkedImageData,
                        selections: {
                            title: String(selections.title),
                            age: /^[1-6]$/.test(String(selections.age)) ? `${selections.age}0` : String(selections.age),
                            channel: String(selections.channel),
                            Style: String(selections.style),
                        },
                        instaText: instaText || "",
                        storeBusinessNumber,
                        type: "manual",
                        time: fullDate,
                    },
                });
            }

            const off = addAppMessageListener((msg) => {
                if (msg?.type !== "SHARE_RESULT") return;
                if (msg?.payload?.platform && msg.payload.platform !== platform) return;

                off?.();
                setSharePending(false);

                const payload = msg.payload || {};
                const ok = payload.success === true;
                const errorCode = payload.error_code;
                const cancelled = payload.cancelled;

                // 1) 성공 → 최종 페이지로 이동
                if (ok) {
                    navigate("/manual/marketing/final", {
                        state: {
                            image: imageData,
                            selections: {
                                title: String(selections.title),
                                age: /^[1-6]$/.test(String(selections.age)) ? `${selections.age}0` : String(selections.age),
                                channel: String(selections.channel),
                                Style: String(selections.style),
                            },
                            instaText: instaText || "",
                            storeBusinessNumber,
                            type: "manual",
                            time: fullDate,
                        },
                    });
                    return;
                }

                // 2) 앱 미설치 + 스토어로 보낸 케이스 → 아무 알럿도 안 띄우고 조용히 종료
                if (errorCode === "app_not_installed") {
                    // 필요하면 토스트 정도는 가능
                    // showToast("앱 설치 후 다시 시도해 주세요.");
                    return;
                }

                // 3) 사용자가 네이티브에서 취소한 경우 → 조용히 종료 (원하면 토스트)
                if (cancelled) {
                    // showToast("공유가 취소되었어요.");
                    return;
                }

                // 4) 그 외 진짜 에러일 때만 알럿
                alert("공유 중 오류가 발생했습니다. 다시 시도해 주세요.");
            });


            setTimeout(() => {
                off?.();
                setSharePending(false);
            }, 150000);
        } catch (error) {
            console.error("백엔드 요청 실패:", error);
            alert("저장 오류");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePopupConfirm = async () => {
        const userId = localStorage.getItem("user_id");

        // "다음에 묻지 않고 진행하기" 체크되어 있고, 아직 re_popup 안 저장된 경우
        if (noPopup && !skipRegenPopup && userId) {
            try {
                await axios.post(
                    `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/re_popup`,
                    {
                        user_id: userId,
                        re_popup: true,
                    }
                );
                localStorage.setItem("re_popup", "true");
                setSkipRegenPopup(true);
            } catch (err) {
                console.log("re_popup save error", err);
            }
        }

        setPopupOpen(false);
        reGenerate(); // 실제 즉시 생성
    };

    const hasChanged =
        selections.title !== initTitle || selections.channel !== initChannel;

    useStrictBackBlock(true);

    return (
        <div className="h-dvh flex flex-col">
            <div
                ref={headerRef}
                className="fixed top-0 left-0 right-0 z-10"
            >
                <HeaderIconL
                    className=""
                    title="생성 완료"
                    icon={home}
                    onIconClick={returnHome}
                />
            </div>

            <div
                className="flex-1 px-[16px] overflow-y-auto"
                style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}
            >
                <div className="flex flex-col pt-[16px] gap-[8px]">
                    <h2 className="text-t2 text-text-default">완성되었습니다✨</h2>
                    <p className="text-b2 text-text-subtler">
                        원하는 이미지 1개를 포스팅해 보세요.
                    </p>
                </div>

                <div className="mt-[24px] w-full bg-surface-light-subtle p-[12px] pl-[16px] rounded-[16px]">
                    <div className="flex items-center justify-between gap-[8px]">
                        <div className="text-t4 text-text-subtle">옵션 설정</div>
                        {/* 🔹 AutoMarketingResult와 동일하게 옵션 변경은 페이지로 이동 */}
                        <Chip
                            chip="d"
                            status="selected"
                            label="변경하기"
                            onClick={() => {
                                if (tokenAll <= 0) {
                                    openTokenPopup();
                                    return;
                                }
                                handleEdit();
                            }}
                        />
                    </div>
                    <SelectOptionButton
                        selectedTags={selectedTags}
                    // onEdit={handleEdit} // 칩 개별 클릭해도 같은 옵션 페이지로 이동
                    />
                </div>

                {hasChanged && (
                    <p className="pt-2 text-sm text-red-500">
                        채널과 주제를 변경 시 이미지 재생성을 먼저 해주세요.
                    </p>
                )}

                <AdsSwiper
                    imageTemplateList={imageSrc ? [imageSrc] : []}
                    content={safeCopyright}
                    title={String(selections.title)}
                    useOption={String(selections.channel)}
                    checkImages={checkImages}
                    handleImageClick={handleImageClick}
                    storeName={store_name}
                    roadName={road_name}
                    weather={main}
                    tag={menu}
                    weekday={weekday}
                    checkedBox={checkedBox}
                    toastBottomOffset={toastOffset}
                />

                {instaText && (
                    <InstaTextArea
                        channel={channelOptions[selections.channel]}
                        value={instaText}
                        onChange={handleTextChange}
                        toastBottomOffset={toastOffset}
                    />
                )}
            </div>

            <div ref={footerRef} className="fixed bottom-0 left-0 right-0 z-10">
                <div className="px-[16px] pt-[12px] bg-surface-light-white">
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            checked={regenWithSameOption}
                            onChange={(e) => {
                                setRegenWithSameOption(e.target.checked);
                            }}
                            className="sr-only"
                        />
                        <img
                            src={regenWithSameOption ? checkOn : checkOff}
                            alt={regenWithSameOption ? "옵션 그대로 다시 생성: 켜짐" : "옵션 그대로 다시 생성: 꺼짐"}
                        />
                        <span
                            className={`pl-[4px] text-b1text-text-subtle`}
                        >
                            옵션 그대로 다시 생성
                        </span>
                    </label>
                </div>
                <SplitRowHalf
                    leftText="다시 생성하기"
                    rightText={isLoading ? "포스팅하기" : "포스팅하기"}
                    leftLeftIcon={retry}
                    onLeft={handleRegenClick}
                    onRight={isLoading ? undefined : onUpload}   // 업로드 중엔 클릭 막기
                    leftType="border"
                    rightType={isLoading ? "gray" : "fill"}      // 🔥 여기!
                    rightDisabled={isLoading}                   // 버튼 자체도 disabled
                />
            </div>

            <TokenPopup
                isOpen={showTokenModal}
                onClose={() => setShowTokenModal(false)}
                origin="aiResult"
                returnTo="/manual/marketing/ai/result"
                returnState={tokenReturnStateRef.current}
            />

            <Popup
                isOpen={popupOpen}
                onClose={onOut}
                title="다시 생성하기"
                subtitle={
                    <>
                        이전 옵션 그대로 생성하시겠습니까?<br />
                        옵션을 변경할 수 있어요.<br />
                        <span className="text-text-danger">
                            * 이미지를 재생성할 경우 토큰이 차감됩니다.
                        </span>
                    </>
                }

                renderButtons={
                    <>
                        <button
                            type="button"
                            className="flex items-center p-[24px] pb-[16px]"
                            onClick={NoPopup}
                        >
                            <img
                                src={noPopup ? checkOn : checkOff}
                                alt=""
                                className="pr-[8px]"
                            />
                            <span
                                className={`text-b1 ${noPopup ? "text-text-default" : "text-text-disabled"}`}
                            >
                                다시 보지 않기
                            </span>

                        </button>
                        <TwinColumn
                            topType={"fill"}
                            topText={"다시 생성하기"}
                            onTop={handlePopupConfirm}
                            bottomType={"empty"}
                            bottomText={"취소"}
                            onBottom={onOut}
                        />
                    </>
                }
            />

            {/* {showRegenPopup && (
                <div
                    className="fixed inset-0 z-10 flex items-center justify-center bg-surface-dark-absolute-dimmed_60"
                    onClick={() => setShowRegenPopup(false)}
                >
                    <div
                        className="w-[312px] px-[24px] pt-[24px] rounded-[24px] bg-surface-light-white-layered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-t3 text-text-default pb-[8px]">
                            다시 생성하기
                        </h2>
                        <p className="text-b2 text-text-subtler">
                            AI가 추천한 옵션 그대로 생성하시겠어요? 옵션
                            설정으로 변경할 수 있어요.
                        </p>
                        <p className="text-b2 text-text-danger">
                            * AI 이미지를 새롭게 생성할 경우 토큰이 차감될 수
                            있습니다.
                        </p>

                        <div className="pt-[24px] flex flex-col gap-[8px] -mx-[12px] pb-[16px]">
                            <TwinColumn
                                topType="fill"
                                topText="옵션 변경하기"
                                onTop={handleRegenChangeOption}
                                bottomType="border"
                                bottomText="옵션 그대로 생성하기"
                                onBottom={reGenerate}
                            />
                        </div>
                    </div>
                </div>
            )} */}
        </div>
    );
};

export default ManualMarketingAIResult;